import Component from '@glimmer/component';
import { t } from 'ember-intl';
import Text from 'tio-ui/components/text';
import { PhCheckCircle, PhCircle, PhFileText } from 'ember-phosphor-icons';
import type TasProgramTemplateModel from 'tio-data/models/tas-program-template';
import {
  getSingleValueForTasField,
  getAllValuesForTasField,
} from 'tio-common/utils/tuition-assistance/fields';

interface DocumentRequirementsSignature {
  Args: {
    /** The program template containing document requirements configuration */
    programTemplate: TasProgramTemplateModel;
    /** Which classification of documents to show requirements for */
    classification: 'COURSE_APPROVAL_DOCUMENTS' | 'COMPLETION_DOCUMENTS';
    /** Number of documents currently uploaded for this classification */
    uploadedCount?: number;
  };
  Element: HTMLDivElement;
}

/**
 * DocumentRequirements - Upfront checklist of required documents
 *
 * Shows users exactly what documents they need to upload BEFORE they start
 * uploading. Reads requirements from the program template configuration.
 *
 * @example
 * ```gts
 * <DocumentRequirements
 *   @programTemplate={{this.programTemplate}}
 *   @classification="COMPLETION_DOCUMENTS"
 *   @uploadedCount={{this.evidenceAssets.length}}
 * />
 * ```
 */
export default class DocumentRequirements extends Component<DocumentRequirementsSignature> {
  get instructionsFieldKey() {
    return this.args.classification === 'COURSE_APPROVAL_DOCUMENTS'
      ? 'COURSE_APPROVAL_DOCUMENTS_STRING'
      : 'COMPLETION_DOCUMENTS_STRING';
  }

  get itemsFieldKey() {
    return this.args.classification === 'COURSE_APPROVAL_DOCUMENTS'
      ? 'COURSE_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD'
      : 'COMPLETION_DOCUMENTS_ATTACHMENT_FIELD';
  }

  get instructions() {
    return getSingleValueForTasField(
      this.instructionsFieldKey,
      this.args.programTemplate.fields
    ) as string | undefined;
  }

  get requiredItems(): string[] {
    const items = getAllValuesForTasField(
      this.itemsFieldKey,
      this.args.programTemplate.fields
    ) as string[];
    return items ?? [];
  }

  get allRequirementsMet() {
    const uploaded = this.args.uploadedCount ?? 0;
    const required = this.requiredItems.length;
    return required > 0 ? uploaded >= required : uploaded > 0;
  }

  <template>
    <div class="rounded-xl border tio-border-default tio-bg-surface p-5 space-y-4" ...attributes>
      <div class="tio-row-tight">
        <PhFileText @size={{20}} class="tio-fg-info" />
        <Text @weight="semibold">
          {{t "tuition_assistance.reimagined.wizard.documents.requirements_title"}}
        </Text>
      </div>

      {{#if this.instructions}}
        <Text @variant="muted" @size="sm">
          {{this.instructions}}
        </Text>
      {{/if}}

      {{#if this.requiredItems.length}}
        <ul class="space-y-2">
          {{#each this.requiredItems as |item|}}
            <li class="tio-row-top-tight">
              {{#if this.allRequirementsMet}}
                <PhCheckCircle @size={{18}} class="tio-fg-success mt-0.5 shrink-0" />
              {{else}}
                <PhCircle @size={{18}} class="tio-fg-muted mt-0.5 shrink-0" />
              {{/if}}
              <Text @size="sm" class="tio-fg-secondary">{{item}}</Text>
            </li>
          {{/each}}
        </ul>
      {{/if}}

      <div class="rounded-lg tio-bg-sunken p-3">
        <Text @variant="muted" @size="xs">
          {{t "tuition_assistance.reimagined.wizard.documents.format_hint"}}
        </Text>
      </div>
    </div>
  </template>
}
